import { pickCopy } from '@/lib/mycio/translations'
import { getMyCIOLanguage, type MyCIOLanguageCode } from '@/lib/mycio/language'

/**
 * /mycio/your-data — page-specific copy for the data-trust deep dive, fully translated.
 * Separate from translations.ts's getMyCIOYourDataCopy (the LANDING section + nav/footer labels);
 * this module owns every user-visible string on the deep-dive page itself.
 *
 * FOUNDER, R2 (2026-09-21): this page is UI, not the legal contract, so it is shown in the member's language.
 * `en` is the REVIEWED English (the factual review that replaced the earlier self-hosting and "never used to
 * train AI" claims); every other language translates that reviewed English, not the retired first draft.
 *
 * COMPLIANCE (every locale, no exceptions):
 *  - Hosted storage and service-provider processing must be described accurately.
 *    Do not assert exclusive self-hosting, absolute secrecy or unverified certifications.
 *  - Name the providers exactly as the English does (Supabase; Paddle, the payment provider).
 *  - Computed, not advice: MyCIO places no trades, moves no money and replaces no professional.
 *  - 'MyCIO' and provider names stay untranslated. Arabic forward arrows point ←, back points →.
 *  - privacyTrust.test.ts checks these facts in every language.
 */
export type MyCIOYourDataPageCopy = {
  metaTitle: string
  metaDescription: string
  back: string
  eyebrow: string
  titleBefore: string
  titleEm: string
  titleAfter: string
  intro: string
  contentsLabel: string
  toc: {
    nothingToConnect: string
    whatIsStored: string
    whatIsNeverStored: string
    roughIsFine: string
    deleteEverything: string
    sharing: string
    protectedHow: string
    computed: string
  }
  nothingToConnect: { p1: string; p2: string }
  whatIsStored: {
    lead: string
    items: string[]
    provenanceBefore: string
    provenanceStrong: string
    provenanceAfter: string
    privacyLinkLabel: string
    provenanceEnd: string
  }
  whatIsNeverStored: {
    items: { lead: string; rest: string }[]
    closing: string
  }
  roughIsFine: { p1Before: string; p1Strong: string; p1After: string; p2: string }
  deleteSection: { p1: string; p2: string }
  sharingSection: {
    heading: string
    p1Before: string
    p1Strong: string
    p1After: string
    p2Before: string
    p2Strong: string
    p2After: string
  }
  protectedSection: { items: { lead: string; rest: string }[] }
  computedSection: { p: string }
  ctas: { sample: string; start: string; note: string }
}

/** Native-speaker review status of the 13 authored languages below (translated in-session from the reviewed English). */
export const NATIVE_REVIEW = {
  reviewed: false,
  authored: '2026-09-21 in-session',
  note: 'every key, all 13 non-English languages: translations of the reviewed English (compliance wording); the Paddle payment sentence re-translated in-session 2026-09-21, unreviewed',
} as const

const en: MyCIOYourDataPageCopy = {
  metaTitle: 'Your data · MyCIO.ai',
  metaDescription: 'How MyCIO handles planning information, hosted storage, payments and service providers.',
  back: '← Back',
  eyebrow: 'Your information, your choices',
  titleBefore: 'Start with what you’re ',
  titleEm: 'comfortable sharing',
  titleAfter: '.',
  intro: 'You can start with estimates and a financial question. MyCIO uses the details you provide to explain supported choices and help you plan practical next steps. No bank connection is needed to start.',
  contentsLabel: 'Contents',
  toc: {
    nothingToConnect: 'Nothing to connect',
    whatIsStored: 'What is stored',
    whatIsNeverStored: 'What you do not need to provide',
    roughIsFine: 'Rough numbers are first-class',
    deleteEverything: 'Review, edit or request deletion',
    sharing: 'Sharing — when you’re ready',
    protectedHow: 'Storage and service providers',
    computed: 'Computed, not advice',
  },
  nothingToConnect: {
    p1: 'Enter balances, income, goals and dates yourself. You do not need to give MyCIO a bank password or brokerage login to build a plan.',
    p2: 'If you use MyCIO through another assistant, information you send through that assistant is also subject to its provider’s terms and privacy settings.',
  },
  whatIsStored: {
    lead: 'Information used to provide the service can include:',
    items: ['Account and sign-in information.', 'The household and financial facts you enter or confirm.',
      'Saved plans, comparisons and conversation context.', 'Operational records needed to run the service and manage purchases.'],
    provenanceBefore: 'Review the planning facts in ', provenanceStrong: '“Your information & sources”',
    provenanceAfter: ' and correct anything that does not reflect your situation. See the ',
    privacyLinkLabel: 'Privacy Policy', provenanceEnd: ' for data handling details.',
  },
  whatIsNeverStored: {
    items: [
      { lead: 'Bank passwords.', rest: 'Do not include bank or brokerage credentials in a question or document.' },
      { lead: 'A connected bank account.', rest: 'You can enter your financial information manually.' },
      { lead: 'Full payment-card details.', rest: 'Card payments are handled by Paddle, our payment provider; we never see or store your full card number. MyCIO receives only the purchase status and transaction references needed to provide and support the service.' },
    ],
    closing: 'Hosting, authentication, payment, error-monitoring and AI service providers process information needed to deliver the service. Optional analytics providers receive data only after you allow analytics. The Privacy Policy explains these roles.',
  },
  roughIsFine: {
    p1Before: 'Estimates can help you get started. Review the ', p1Strong: 'assumptions and missing details',
    p1After: ' before relying on a comparison.',
    p2: 'Some questions need exact dates, account ownership or tax details. MyCIO may ask for those facts or explain that a scenario is outside its coverage.',
  },
  deleteSection: {
    p1: 'Use the controls in your plan to review or remove individual planning facts. Settings also lets you download your data or request deletion of your account and active planning data.',
    p2: 'Limited payment, fraud-prevention, security or legal records may need to remain after account deletion, and deletion from provider backups follows the provider’s backup lifecycle. The Privacy Policy explains these exceptions; contact support with a specific request.',
  },
  sharingSection: {
    heading: 'Choose what to share',
    p1Before: 'Review a ', p1Strong: 'shared plan summary',
    p1After: ' before sending its link to a partner or professional; summaries can contain financial information.',
    p2Before: 'Use the available ', p2Strong: 'sharing controls',
    p2After: ' to manage access, and share links only with people you intend to see the result.',
  },
  protectedSection: { items: [
    { lead: 'Hosted services.', rest: 'MyCIO uses hosted Supabase for account, database and storage services, alongside application hosting and other service providers.' },
    { lead: 'Access controls.', rest: 'Authentication and database access policies restrict access to account information; service providers also process data needed to operate the service.' },
    { lead: 'Provider-specific handling.', rest: 'The Privacy Policy describes service-provider processing. Ask support if you need details before sharing sensitive information.' },
  ] },
  computedSection: { p: 'MyCIO explains modeled outcomes and helps you work out next steps. It does not place trades, move money or replace an investment, tax or legal professional. You decide what to do and verify important actions before carrying them out.' },
  ctas: { sample: 'See an example', start: 'Start with a question', note: 'Examples are illustrative; their assumptions and limits are shown alongside them.' },
}

const es: MyCIOYourDataPageCopy = {
  metaTitle: 'Tus datos · MyCIO.ai',
  metaDescription: 'Cómo trata MyCIO la información de planificación, el almacenamiento alojado, los pagos y los proveedores de servicios.',
  back: '← Volver',
  eyebrow: 'Tu información, tus decisiones',
  titleBefore: 'Empieza con lo que te ',
  titleEm: 'resulte cómodo compartir',
  titleAfter: '.',
  intro: 'Puedes empezar con estimaciones y una pregunta financiera. MyCIO usa los datos que aportas para explicar las opciones admitidas y ayudarte a planificar próximos pasos prácticos. No hace falta conectar ningún banco para empezar.',
  contentsLabel: 'Contenido',
  toc: {
    nothingToConnect: 'Nada que conectar',
    whatIsStored: 'Qué se guarda',
    whatIsNeverStored: 'Lo que no necesitas aportar',
    roughIsFine: 'Las cifras aproximadas valen',
    deleteEverything: 'Revisar, editar o solicitar la eliminación',
    sharing: 'Compartir, cuando estés listo',
    protectedHow: 'Almacenamiento y proveedores de servicios',
    computed: 'Calculado, no asesoramiento',
  },
  nothingToConnect: {
    p1: 'Introduce tú mismo saldos, ingresos, objetivos y fechas. No necesitas dar a MyCIO la contraseña de tu banco ni el acceso a tu bróker para elaborar un plan.',
    p2: 'Si usas MyCIO a través de otro asistente, la información que envíes por ese asistente también está sujeta a las condiciones y a la configuración de privacidad de su proveedor.',
  },
  whatIsStored: {
    lead: 'La información utilizada para prestar el servicio puede incluir:',
    items: ['Información de la cuenta y de inicio de sesión.', 'Los datos del hogar y financieros que introduces o confirmas.',
      'Planes guardados, comparaciones y contexto de las conversaciones.', 'Registros operativos necesarios para prestar el servicio y gestionar las compras.'],
    provenanceBefore: 'Revisa los datos de planificación en ', provenanceStrong: '«Tu información y fuentes»',
    provenanceAfter: ' y corrige todo lo que no refleje tu situación. Consulta la ',
    privacyLinkLabel: 'Política de privacidad', provenanceEnd: ' para conocer los detalles del tratamiento de datos.',
  },
  whatIsNeverStored: {
    items: [
      { lead: 'Contraseñas bancarias.', rest: 'No incluyas credenciales del banco ni del bróker en una pregunta o en un documento.' },
      { lead: 'Una cuenta bancaria conectada.', rest: 'Puedes introducir tu información financiera manualmente.' },
      { lead: 'Los datos completos de la tarjeta de pago.', rest: 'Los pagos con tarjeta los gestiona Paddle, nuestro proveedor de pagos; nunca vemos ni guardamos el número completo de tu tarjeta. MyCIO solo recibe el estado de la compra y las referencias de la transacción necesarias para prestar el servicio y darle soporte.' },
    ],
    closing: 'Los proveedores de alojamiento, autenticación, pagos, monitorización de errores e IA tratan la información necesaria para prestar el servicio. Los proveedores de analítica opcionales solo reciben datos después de que autorices la analítica. La Política de privacidad explica estas funciones.',
  },
  roughIsFine: {
    p1Before: 'Las estimaciones te ayudan a empezar. Revisa los ', p1Strong: 'supuestos y los datos que faltan',
    p1After: ' antes de apoyarte en una comparación.',
    p2: 'Algunas preguntas requieren fechas exactas, la titularidad de las cuentas o datos fiscales. MyCIO puede pedirte esos datos o explicarte que un escenario queda fuera de su cobertura.',
  },
  deleteSection: {
    p1: 'Usa los controles de tu plan para revisar o eliminar datos de planificación concretos. En Ajustes también puedes descargar tus datos o solicitar la eliminación de tu cuenta y de los datos de planificación activos.',
    p2: 'Es posible que un número limitado de registros de pago, prevención del fraude, seguridad o de carácter legal deba conservarse tras eliminar la cuenta, y la eliminación de las copias de seguridad de los proveedores sigue el ciclo de vida de esas copias. La Política de privacidad explica estas excepciones; contacta con soporte con una solicitud concreta.',
  },
  sharingSection: {
    heading: 'Elige qué compartir',
    p1Before: 'Revisa el ', p1Strong: 'resumen del plan compartido',
    p1After: ' antes de enviar su enlace a tu pareja o a un profesional; los resúmenes pueden contener información financiera.',
    p2Before: 'Usa los ', p2Strong: 'controles para compartir',
    p2After: ' disponibles para gestionar el acceso, y comparte los enlaces solo con las personas que quieras que vean el resultado.',
  },
  protectedSection: { items: [
    { lead: 'Servicios alojados.', rest: 'MyCIO usa Supabase alojado para los servicios de cuentas, base de datos y almacenamiento, junto con el alojamiento de la aplicación y otros proveedores de servicios.' },
    { lead: 'Controles de acceso.', rest: 'La autenticación y las políticas de acceso a la base de datos restringen el acceso a la información de la cuenta; los proveedores de servicios también tratan los datos necesarios para operar el servicio.' },
    { lead: 'Tratamiento por cada proveedor.', rest: 'La Política de privacidad describe el tratamiento que realizan los proveedores de servicios. Pregunta a soporte si necesitas detalles antes de compartir información sensible.' },
  ] },
  computedSection: { p: 'MyCIO explica resultados modelizados y te ayuda a definir los próximos pasos. No ejecuta operaciones, no mueve dinero ni sustituye a un profesional de inversiones, fiscal o jurídico. Tú decides qué hacer y verificas las acciones importantes antes de llevarlas a cabo.' },
  ctas: { sample: 'Ver un ejemplo', start: 'Empezar con una pregunta', note: 'Los ejemplos son ilustrativos; sus supuestos y límites se muestran junto a ellos.' },
}

const pt: MyCIOYourDataPageCopy = {
  metaTitle: 'Seus dados · MyCIO.ai',
  metaDescription: 'Como o MyCIO trata as informações de planejamento, o armazenamento hospedado, os pagamentos e os prestadores de serviços.',
  back: '← Voltar',
  eyebrow: 'Suas informações, suas escolhas',
  titleBefore: 'Comece pelo que você se ',
  titleEm: 'sente à vontade para compartilhar',
  titleAfter: '.',
  intro: 'Você pode começar com estimativas e uma pergunta financeira. O MyCIO usa os dados que você fornece para explicar as opções suportadas e ajudar a planejar próximos passos práticos. Não é preciso conectar nenhum banco para começar.',
  contentsLabel: 'Conteúdo',
  toc: {
    nothingToConnect: 'Nada para conectar',
    whatIsStored: 'O que é armazenado',
    whatIsNeverStored: 'O que você não precisa fornecer',
    roughIsFine: 'Números aproximados valem',
    deleteEverything: 'Revisar, editar ou solicitar a exclusão',
    sharing: 'Compartilhar, quando você quiser',
    protectedHow: 'Armazenamento e prestadores de serviços',
    computed: 'Calculado, não é aconselhamento',
  },
  nothingToConnect: {
    p1: 'Informe você mesmo saldos, renda, objetivos e datas. Você não precisa fornecer ao MyCIO a senha do banco nem o login da corretora para montar um plano.',
    p2: 'Se você usa o MyCIO por meio de outro assistente, as informações que envia por esse assistente também estão sujeitas aos termos e às configurações de privacidade do respectivo provedor.',
  },
  whatIsStored: {
    lead: 'As informações usadas para prestar o serviço podem incluir:',
    items: ['Informações da conta e de login.', 'Os dados da família e financeiros que você informa ou confirma.',
      'Planos salvos, comparações e contexto das conversas.', 'Registros operacionais necessários para operar o serviço e gerenciar compras.'],
    provenanceBefore: 'Revise os dados de planejamento em ', provenanceStrong: '“Suas informações e fontes”',
    provenanceAfter: ' e corrija o que não refletir a sua situação. Consulte a ',
    privacyLinkLabel: 'Política de Privacidade', provenanceEnd: ' para os detalhes sobre o tratamento de dados.',
  },
  whatIsNeverStored: {
    items: [
      { lead: 'Senhas bancárias.', rest: 'Não inclua credenciais do banco ou da corretora em uma pergunta ou documento.' },
      { lead: 'Uma conta bancária conectada.', rest: 'Você pode informar seus dados financeiros manualmente.' },
      { lead: 'Os dados completos do cartão de pagamento.', rest: 'Os pagamentos com cartão são processados pela Paddle, nosso provedor de pagamentos; nunca vemos nem armazenamos o número completo do seu cartão. O MyCIO recebe apenas o status da compra e as referências da transação necessárias para prestar o serviço e dar suporte.' },
    ],
    closing: 'Os prestadores de hospedagem, autenticação, pagamento, monitoramento de erros e IA tratam as informações necessárias para prestar o serviço. Os provedores de análise opcionais só recebem dados depois que você autoriza a análise. A Política de Privacidade explica esses papéis.',
  },
  roughIsFine: {
    p1Before: 'Estimativas ajudam você a começar. Revise as ', p1Strong: 'premissas e os dados que faltam',
    p1After: ' antes de se basear em uma comparação.',
    p2: 'Algumas perguntas exigem datas exatas, a titularidade das contas ou dados fiscais. O MyCIO pode pedir esses dados ou explicar que um cenário está fora da sua cobertura.',
  },
  deleteSection: {
    p1: 'Use os controles do seu plano para revisar ou remover dados de planejamento específicos. Em Configurações você também pode baixar seus dados ou solicitar a exclusão da sua conta e dos dados de planejamento ativos.',
    p2: 'Alguns registros limitados de pagamento, prevenção a fraudes, segurança ou de natureza legal podem precisar ser mantidos após a exclusão da conta, e a exclusão dos backups dos prestadores segue o ciclo de vida desses backups. A Política de Privacidade explica essas exceções; entre em contato com o suporte com uma solicitação específica.',
  },
  sharingSection: {
    heading: 'Escolha o que compartilhar',
    p1Before: 'Revise o ', p1Strong: 'resumo do plano compartilhado',
    p1After: ' antes de enviar o link a um parceiro ou profissional; os resumos podem conter informações financeiras.',
    p2Before: 'Use os ', p2Strong: 'controles de compartilhamento',
    p2After: ' disponíveis para gerenciar o acesso e compartilhe links apenas com as pessoas que você quer que vejam o resultado.',
  },
  protectedSection: { items: [
    { lead: 'Serviços hospedados.', rest: 'O MyCIO usa o Supabase hospedado para os serviços de conta, banco de dados e armazenamento, junto com a hospedagem da aplicação e outros prestadores de serviços.' },
    { lead: 'Controles de acesso.', rest: 'A autenticação e as políticas de acesso ao banco de dados restringem o acesso às informações da conta; os prestadores de serviços também tratam os dados necessários para operar o serviço.' },
    { lead: 'Tratamento por prestador.', rest: 'A Política de Privacidade descreve o tratamento feito pelos prestadores de serviços. Fale com o suporte se precisar de detalhes antes de compartilhar informações sensíveis.' },
  ] },
  computedSection: { p: 'O MyCIO explica resultados modelados e ajuda você a definir os próximos passos. Ele não executa operações, não movimenta dinheiro e não substitui um profissional de investimentos, tributário ou jurídico. Você decide o que fazer e verifica as ações importantes antes de executá-las.' },
  ctas: { sample: 'Ver um exemplo', start: 'Começar com uma pergunta', note: 'Os exemplos são ilustrativos; as premissas e os limites de cada um aparecem ao lado.' },
}

const fr: MyCIOYourDataPageCopy = {
  metaTitle: 'Vos données · MyCIO.ai',
  metaDescription: 'Comment MyCIO traite les informations de planification, le stockage hébergé, les paiements et les prestataires de services.',
  back: '← Retour',
  eyebrow: 'Vos informations, vos choix',
  titleBefore: 'Commencez par ce que vous êtes ',
  titleEm: 'à l’aise de partager',
  titleAfter: '.',
  intro: 'Vous pouvez commencer avec des estimations et une question financière. MyCIO utilise les informations que vous fournissez pour expliquer les options prises en charge et vous aider à planifier des prochaines étapes concrètes. Aucune connexion bancaire n’est nécessaire pour commencer.',
  contentsLabel: 'Sommaire',
  toc: {
    nothingToConnect: 'Rien à connecter',
    whatIsStored: 'Ce qui est conservé',
    whatIsNeverStored: 'Ce que vous n’avez pas à fournir',
    roughIsFine: 'Les chiffres approximatifs sont les bienvenus',
    deleteEverything: 'Consulter, modifier ou demander la suppression',
    sharing: 'Partager, quand vous êtes prêt',
    protectedHow: 'Stockage et prestataires de services',
    computed: 'Calculé, pas un conseil',
  },
  nothingToConnect: {
    p1: 'Saisissez vous-même soldes, revenus, objectifs et dates. Vous n’avez pas besoin de donner à MyCIO un mot de passe bancaire ni un identifiant de courtier pour établir un plan.',
    p2: 'Si vous utilisez MyCIO via un autre assistant, les informations que vous transmettez par cet assistant sont également soumises aux conditions et aux paramètres de confidentialité de son fournisseur.',
  },
  whatIsStored: {
    lead: 'Les informations utilisées pour fournir le service peuvent comprendre :',
    items: ['Les informations de compte et de connexion.', 'Les données du foyer et les données financières que vous saisissez ou confirmez.',
      'Les plans enregistrés, les comparaisons et le contexte des conversations.', 'Les registres opérationnels nécessaires au fonctionnement du service et à la gestion des achats.'],
    provenanceBefore: 'Vérifiez les données de planification dans ', provenanceStrong: '« Vos informations et sources »',
    provenanceAfter: ' et corrigez tout ce qui ne reflète pas votre situation. Consultez la ',
    privacyLinkLabel: 'Politique de confidentialité', provenanceEnd: ' pour le détail du traitement des données.',
  },
  whatIsNeverStored: {
    items: [
      { lead: 'Mots de passe bancaires.', rest: 'N’indiquez pas d’identifiants bancaires ou de courtage dans une question ou un document.' },
      { lead: 'Un compte bancaire connecté.', rest: 'Vous pouvez saisir vos informations financières manuellement.' },
      { lead: 'Les coordonnées complètes de la carte de paiement.', rest: 'Les paiements par carte sont traités par Paddle, notre prestataire de paiement ; nous ne voyons ni ne conservons jamais le numéro complet de votre carte. MyCIO reçoit uniquement le statut de l’achat et les références de transaction nécessaires pour fournir le service et en assurer le support.' },
    ],
    closing: 'Les prestataires d’hébergement, d’authentification, de paiement, de surveillance des erreurs et d’IA traitent les informations nécessaires à la fourniture du service. Les fournisseurs d’analyse facultatifs ne reçoivent des données qu’après votre accord pour l’analyse. La Politique de confidentialité explique ces rôles.',
  },
  roughIsFine: {
    p1Before: 'Des estimations suffisent pour commencer. Vérifiez les ', p1Strong: 'hypothèses et les informations manquantes',
    p1After: ' avant de vous fier à une comparaison.',
    p2: 'Certaines questions exigent des dates exactes, la titularité des comptes ou des éléments fiscaux. MyCIO peut vous demander ces informations ou vous expliquer qu’un scénario sort de son périmètre.',
  },
  deleteSection: {
    p1: 'Utilisez les commandes de votre plan pour consulter ou supprimer des données de planification. Les Paramètres vous permettent aussi de télécharger vos données ou de demander la suppression de votre compte et de vos données de planification actives.',
    p2: 'Certains enregistrements limités liés aux paiements, à la prévention de la fraude, à la sécurité ou à des obligations légales peuvent devoir être conservés après la suppression du compte, et la suppression des sauvegardes des prestataires suit le cycle de vie de ces sauvegardes. La Politique de confidentialité explique ces exceptions ; contactez le support avec une demande précise.',
  },
  sharingSection: {
    heading: 'Choisissez ce que vous partagez',
    p1Before: 'Relisez le ', p1Strong: 'résumé de plan partagé',
    p1After: ' avant d’en envoyer le lien à votre conjoint ou à un professionnel ; les résumés peuvent contenir des informations financières.',
    p2Before: 'Utilisez les ', p2Strong: 'contrôles de partage',
    p2After: ' disponibles pour gérer l’accès, et ne partagez les liens qu’avec les personnes censées voir le résultat.',
  },
  protectedSection: { items: [
    { lead: 'Services hébergés.', rest: 'MyCIO utilise Supabase en mode hébergé pour les services de compte, de base de données et de stockage, aux côtés de l’hébergement de l’application et d’autres prestataires de services.' },
    { lead: 'Contrôles d’accès.', rest: 'L’authentification et les règles d’accès à la base de données restreignent l’accès aux informations du compte ; les prestataires de services traitent aussi les données nécessaires au fonctionnement du service.' },
    { lead: 'Traitement propre à chaque prestataire.', rest: 'La Politique de confidentialité décrit les traitements effectués par les prestataires de services. Interrogez le support si vous avez besoin de précisions avant de partager des informations sensibles.' },
  ] },
  computedSection: { p: 'MyCIO explique des résultats modélisés et vous aide à définir vos prochaines étapes. Il ne passe pas d’ordres, ne déplace pas d’argent et ne remplace pas un professionnel de l’investissement, de la fiscalité ou du droit. Vous décidez de ce que vous faites et vérifiez les actions importantes avant de les mettre en œuvre.' },
  ctas: { sample: 'Voir un exemple', start: 'Commencer par une question', note: 'Les exemples sont illustratifs ; leurs hypothèses et leurs limites sont présentées à côté.' },
}

const de: MyCIOYourDataPageCopy = {
  metaTitle: 'Ihre Daten · MyCIO.ai',
  metaDescription: 'Wie MyCIO mit Planungsinformationen, gehostetem Speicher, Zahlungen und Dienstleistern umgeht.',
  back: '← Zurück',
  eyebrow: 'Ihre Angaben, Ihre Entscheidung',
  titleBefore: 'Beginnen Sie mit dem, was Sie ',
  titleEm: 'gern teilen möchten',
  titleAfter: '.',
  intro: 'Sie können mit Schätzwerten und einer Finanzfrage beginnen. MyCIO nutzt die Angaben, die Sie machen, um unterstützte Optionen zu erläutern und Ihnen bei der Planung konkreter nächster Schritte zu helfen. Für den Einstieg ist keine Bankverbindung nötig.',
  contentsLabel: 'Inhalt',
  toc: {
    nothingToConnect: 'Nichts zu verbinden',
    whatIsStored: 'Was gespeichert wird',
    whatIsNeverStored: 'Was Sie nicht angeben müssen',
    roughIsFine: 'Grobe Zahlen sind vollwertig',
    deleteEverything: 'Prüfen, bearbeiten oder Löschung beantragen',
    sharing: 'Teilen – wenn Sie so weit sind',
    protectedHow: 'Speicherung und Dienstleister',
    computed: 'Berechnet, keine Beratung',
  },
  nothingToConnect: {
    p1: 'Geben Sie Kontostände, Einkommen, Ziele und Termine selbst ein. Für einen Plan müssen Sie MyCIO weder ein Bankpasswort noch einen Depotzugang überlassen.',
    p2: 'Wenn Sie MyCIO über einen anderen Assistenten nutzen, unterliegen die Informationen, die Sie über diesen Assistenten senden, auch den Bedingungen und Datenschutzeinstellungen seines Anbieters.',
  },
  whatIsStored: {
    lead: 'Zu den Informationen, die für die Erbringung des Dienstes genutzt werden, können gehören:',
    items: ['Konto- und Anmeldedaten.', 'Die Haushalts- und Finanzangaben, die Sie eingeben oder bestätigen.',
      'Gespeicherte Pläne, Vergleiche und Gesprächskontext.', 'Betriebsaufzeichnungen, die für den Betrieb des Dienstes und die Abwicklung von Käufen nötig sind.'],
    provenanceBefore: 'Prüfen Sie die Planungsangaben unter ', provenanceStrong: '„Ihre Angaben & Quellen“',
    provenanceAfter: ' und korrigieren Sie alles, was Ihre Situation nicht zutreffend wiedergibt. Einzelheiten zur Datenverarbeitung finden Sie in der ',
    privacyLinkLabel: 'Datenschutzerklärung', provenanceEnd: '.',
  },
  whatIsNeverStored: {
    items: [
      { lead: 'Bankpasswörter.', rest: 'Geben Sie in einer Frage oder einem Dokument keine Zugangsdaten zu Bank oder Depot an.' },
      { lead: 'Ein verbundenes Bankkonto.', rest: 'Sie können Ihre Finanzangaben manuell eingeben.' },
      { lead: 'Vollständige Zahlungskartendaten.', rest: 'Kartenzahlungen wickelt Paddle ab, unser Zahlungsdienstleister; wir sehen oder speichern Ihre vollständige Kartennummer nie. MyCIO erhält nur den Kaufstatus und die Transaktionsreferenzen, die für die Erbringung und den Support des Dienstes nötig sind.' },
    ],
    closing: 'Dienstleister für Hosting, Authentifizierung, Zahlung, Fehlerüberwachung und KI verarbeiten die Informationen, die für die Erbringung des Dienstes nötig sind. Optionale Analyseanbieter erhalten Daten erst, nachdem Sie Analysen zugelassen haben. Die Datenschutzerklärung erläutert diese Rollen.',
  },
  roughIsFine: {
    p1Before: 'Schätzwerte erleichtern den Einstieg. Prüfen Sie die ', p1Strong: 'Annahmen und fehlenden Angaben',
    p1After: ', bevor Sie sich auf einen Vergleich stützen.',
    p2: 'Manche Fragen erfordern genaue Daten, die Kontoinhaberschaft oder steuerliche Angaben. MyCIO fragt dann nach diesen Angaben oder erklärt, dass ein Szenario außerhalb seines Abdeckungsbereichs liegt.',
  },
  deleteSection: {
    p1: 'Mit den Steuerelementen in Ihrem Plan können Sie einzelne Planungsangaben prüfen oder entfernen. In den Einstellungen können Sie außerdem Ihre Daten herunterladen oder die Löschung Ihres Kontos und Ihrer aktiven Planungsdaten beantragen.',
    p2: 'Bestimmte Aufzeichnungen zu Zahlungen, Betrugsprävention, Sicherheit oder aus rechtlichen Gründen müssen nach der Kontolöschung unter Umständen in begrenztem Umfang erhalten bleiben, und die Löschung aus den Backups der Anbieter folgt deren Backup-Zyklus. Die Datenschutzerklärung erläutert diese Ausnahmen; wenden Sie sich mit einem konkreten Anliegen an den Support.',
  },
  sharingSection: {
    heading: 'Entscheiden Sie, was Sie teilen',
    p1Before: 'Prüfen Sie eine ', p1Strong: 'geteilte Planübersicht',
    p1After: ', bevor Sie den Link an Ihre Partnerin, Ihren Partner oder eine Fachperson senden; Übersichten können Finanzinformationen enthalten.',
    p2Before: 'Nutzen Sie die verfügbaren ', p2Strong: 'Freigabeeinstellungen',
    p2After: ', um den Zugriff zu steuern, und teilen Sie Links nur mit Personen, die das Ergebnis sehen sollen.',
  },
  protectedSection: { items: [
    { lead: 'Gehostete Dienste.', rest: 'MyCIO nutzt gehostetes Supabase für Konto-, Datenbank- und Speicherdienste, neben dem Hosting der Anwendung und weiteren Dienstleistern.' },
    { lead: 'Zugriffskontrollen.', rest: 'Authentifizierung und Zugriffsrichtlinien der Datenbank beschränken den Zugriff auf Kontoinformationen; Dienstleister verarbeiten ebenfalls Daten, die für den Betrieb des Dienstes nötig sind.' },
    { lead: 'Verarbeitung je Anbieter.', rest: 'Die Datenschutzerklärung beschreibt die Verarbeitung durch Dienstleister. Fragen Sie den Support, wenn Sie Einzelheiten benötigen, bevor Sie sensible Informationen teilen.' },
  ] },
  computedSection: { p: 'MyCIO erläutert modellierte Ergebnisse und hilft Ihnen, nächste Schritte zu erarbeiten. Es führt keine Transaktionen aus, bewegt kein Geld und ersetzt keine Fachperson für Anlage-, Steuer- oder Rechtsfragen. Sie entscheiden, was Sie tun, und prüfen wichtige Schritte, bevor Sie sie umsetzen.' },
  ctas: { sample: 'Beispiel ansehen', start: 'Mit einer Frage beginnen', note: 'Beispiele dienen der Veranschaulichung; ihre Annahmen und Grenzen werden daneben angezeigt.' },
}

const it: MyCIOYourDataPageCopy = {
  metaTitle: 'I tuoi dati · MyCIO.ai',
  metaDescription: 'Come MyCIO tratta le informazioni di pianificazione, l’archiviazione in hosting, i pagamenti e i fornitori di servizi.',
  back: '← Indietro',
  eyebrow: 'Le tue informazioni, le tue scelte',
  titleBefore: 'Parti da ciò che ti ',
  titleEm: 'senti a tuo agio a condividere',
  titleAfter: '.',
  intro: 'Puoi iniziare con delle stime e una domanda finanziaria. MyCIO usa i dati che fornisci per spiegare le opzioni supportate e aiutarti a pianificare passi successivi concreti. Per iniziare non serve collegare alcuna banca.',
  contentsLabel: 'Indice',
  toc: {
    nothingToConnect: 'Niente da collegare',
    whatIsStored: 'Cosa viene conservato',
    whatIsNeverStored: 'Cosa non devi fornire',
    roughIsFine: 'Le cifre approssimative vanno benissimo',
    deleteEverything: 'Verificare, modificare o chiedere la cancellazione',
    sharing: 'Condividere, quando sei pronto',
    protectedHow: 'Archiviazione e fornitori di servizi',
    computed: 'Calcolato, non consulenza',
  },
  nothingToConnect: {
    p1: 'Inserisci tu saldi, redditi, obiettivi e date. Per costruire un piano non devi fornire a MyCIO la password della banca né le credenziali del tuo intermediario.',
    p2: 'Se usi MyCIO tramite un altro assistente, le informazioni che invii attraverso quell’assistente sono soggette anche ai termini e alle impostazioni di privacy del relativo fornitore.',
  },
  whatIsStored: {
    lead: 'Le informazioni usate per fornire il servizio possono comprendere:',
    items: ['Dati dell’account e di accesso.', 'I dati del nucleo familiare e finanziari che inserisci o confermi.',
      'Piani salvati, confronti e contesto delle conversazioni.', 'Registrazioni operative necessarie per erogare il servizio e gestire gli acquisti.'],
    provenanceBefore: 'Verifica i dati di pianificazione in ', provenanceStrong: '«Le tue informazioni e fonti»',
    provenanceAfter: ' e correggi ciò che non rispecchia la tua situazione. Consulta l’',
    privacyLinkLabel: 'Informativa sulla privacy', provenanceEnd: ' per i dettagli sul trattamento dei dati.',
  },
  whatIsNeverStored: {
    items: [
      { lead: 'Password bancarie.', rest: 'Non inserire credenziali della banca o dell’intermediario in una domanda o in un documento.' },
      { lead: 'Un conto bancario collegato.', rest: 'Puoi inserire le tue informazioni finanziarie manualmente.' },
      { lead: 'I dati completi della carta di pagamento.', rest: 'I pagamenti con carta sono gestiti da Paddle, il nostro fornitore di servizi di pagamento; non vediamo né conserviamo mai il numero completo della tua carta. MyCIO riceve solo lo stato dell’acquisto e i riferimenti della transazione necessari per fornire il servizio e l’assistenza.' },
    ],
    closing: 'I fornitori di hosting, autenticazione, pagamento, monitoraggio degli errori e IA trattano le informazioni necessarie per erogare il servizio. I fornitori di analisi facoltativi ricevono dati solo dopo che hai autorizzato l’analisi. L’Informativa sulla privacy spiega questi ruoli.',
  },
  roughIsFine: {
    p1Before: 'Le stime ti aiutano a iniziare. Verifica le ', p1Strong: 'ipotesi e i dati mancanti',
    p1After: ' prima di basarti su un confronto.',
    p2: 'Alcune domande richiedono date esatte, l’intestazione dei conti o dati fiscali. MyCIO può chiederti questi dati o spiegarti che uno scenario non rientra nella sua copertura.',
  },
  deleteSection: {
    p1: 'Usa i comandi del tuo piano per verificare o rimuovere singoli dati di pianificazione. Dalle Impostazioni puoi anche scaricare i tuoi dati o chiedere la cancellazione dell’account e dei dati di pianificazione attivi.',
    p2: 'Alcune registrazioni limitate relative a pagamenti, prevenzione delle frodi, sicurezza o obblighi di legge potrebbero dover restare anche dopo la cancellazione dell’account, e la cancellazione dai backup dei fornitori segue il ciclo di vita di tali backup. L’Informativa sulla privacy spiega queste eccezioni; contatta l’assistenza con una richiesta specifica.',
  },
  sharingSection: {
    heading: 'Scegli cosa condividere',
    p1Before: 'Rileggi il ', p1Strong: 'riepilogo del piano condiviso',
    p1After: ' prima di inviarne il link al partner o a un professionista; i riepiloghi possono contenere informazioni finanziarie.',
    p2Before: 'Usa i ', p2Strong: 'controlli di condivisione',
    p2After: ' disponibili per gestire l’accesso e condividi i link solo con le persone che devono vedere il risultato.',
  },
  protectedSection: { items: [
    { lead: 'Servizi in hosting.', rest: 'MyCIO usa Supabase in hosting per i servizi di account, database e archiviazione, insieme all’hosting dell’applicazione e ad altri fornitori di servizi.' },
    { lead: 'Controlli di accesso.', rest: 'L’autenticazione e le regole di accesso al database limitano l’accesso alle informazioni dell’account; anche i fornitori di servizi trattano i dati necessari per gestire il servizio.' },
    { lead: 'Trattamento specifico di ogni fornitore.', rest: 'L’Informativa sulla privacy descrive il trattamento svolto dai fornitori di servizi. Chiedi all’assistenza se ti servono dettagli prima di condividere informazioni sensibili.' },
  ] },
  computedSection: { p: 'MyCIO spiega risultati modellizzati e ti aiuta a definire i passi successivi. Non esegue operazioni, non sposta denaro e non sostituisce un professionista degli investimenti, fiscale o legale. Sei tu a decidere cosa fare e a verificare le azioni importanti prima di metterle in pratica.' },
  ctas: { sample: 'Vedi un esempio', start: 'Inizia con una domanda', note: 'Gli esempi sono illustrativi; le loro ipotesi e i loro limiti sono indicati accanto.' },
}

const ru: MyCIOYourDataPageCopy = {
  metaTitle: 'Ваши данные · MyCIO.ai',
  metaDescription: 'Как MyCIO обращается с информацией для планирования, размещённым хранилищем, платежами и поставщиками услуг.',
  back: '← Назад',
  eyebrow: 'Ваши сведения — ваш выбор',
  titleBefore: 'Начните с того, чем вам ',
  titleEm: 'комфортно поделиться',
  titleAfter: '.',
  intro: 'Можно начать с приблизительных оценок и одного финансового вопроса. MyCIO использует предоставленные вами сведения, чтобы разъяснить поддерживаемые варианты и помочь спланировать практические следующие шаги. Для начала подключать банк не нужно.',
  contentsLabel: 'Содержание',
  toc: {
    nothingToConnect: 'Ничего не нужно подключать',
    whatIsStored: 'Что хранится',
    whatIsNeverStored: 'Что предоставлять не нужно',
    roughIsFine: 'Приблизительные цифры полноценны',
    deleteEverything: 'Просмотр, правка и запрос на удаление',
    sharing: 'Доступ другим — когда будете готовы',
    protectedHow: 'Хранение и поставщики услуг',
    computed: 'Расчёт, а не консультация',
  },
  nothingToConnect: {
    p1: 'Вводите остатки, доходы, цели и даты сами. Чтобы составить план, не нужно сообщать MyCIO пароль от банка или логин брокерского счёта.',
    p2: 'Если вы пользуетесь MyCIO через другого ассистента, сведения, которые вы отправляете через него, также подпадают под условия и настройки конфиденциальности его поставщика.',
  },
  whatIsStored: {
    lead: 'Сведения, используемые для оказания услуги, могут включать:',
    items: ['Данные учётной записи и входа.', 'Сведения о домохозяйстве и финансах, которые вы вводите или подтверждаете.',
      'Сохранённые планы, сравнения и контекст бесед.', 'Служебные записи, необходимые для работы сервиса и оформления покупок.'],
    provenanceBefore: 'Проверьте данные для планирования в разделе ', provenanceStrong: '«Ваши сведения и источники»',
    provenanceAfter: ' и исправьте всё, что не соответствует вашей ситуации. Подробности обработки данных — в ',
    privacyLinkLabel: 'Политике конфиденциальности', provenanceEnd: '.',
  },
  whatIsNeverStored: {
    items: [
      { lead: 'Банковские пароли.', rest: 'Не указывайте учётные данные банка или брокера в вопросе или документе.' },
      { lead: 'Подключённый банковский счёт.', rest: 'Финансовые сведения можно ввести вручную.' },
      { lead: 'Полные данные платёжной карты.', rest: 'Платежи картой обрабатывает Paddle, наш платёжный провайдер; мы никогда не видим и не храним полный номер вашей карты. MyCIO получает только статус покупки и реквизиты транзакции, необходимые для оказания услуги и поддержки.' },
    ],
    closing: 'Поставщики хостинга, аутентификации, платежей, мониторинга ошибок и ИИ обрабатывают сведения, необходимые для оказания услуги. Необязательные поставщики аналитики получают данные только после того, как вы разрешите аналитику. Эти роли описаны в Политике конфиденциальности.',
  },
  roughIsFine: {
    p1Before: 'Для начала достаточно оценок. Прежде чем опираться на сравнение, проверьте ', p1Strong: 'допущения и недостающие сведения',
    p1After: '.',
    p2: 'Для некоторых вопросов нужны точные даты, сведения о владельцах счетов или налоговые данные. MyCIO может запросить их или пояснить, что сценарий выходит за рамки его охвата.',
  },
  deleteSection: {
    p1: 'С помощью элементов управления в плане можно просматривать и удалять отдельные данные для планирования. В настройках также можно скачать свои данные или запросить удаление учётной записи и активных данных планирования.',
    p2: 'Ограниченный набор записей о платежах, предотвращении мошенничества, безопасности или юридических записей может потребоваться сохранить после удаления учётной записи, а удаление из резервных копий поставщиков происходит в соответствии с их жизненным циклом. Эти исключения описаны в Политике конфиденциальности; с конкретным запросом обращайтесь в поддержку.',
  },
  sharingSection: {
    heading: 'Решайте, чем делиться',
    p1Before: 'Проверьте ', p1Strong: 'общую сводку плана',
    p1After: ', прежде чем отправлять ссылку партнёру или специалисту: сводки могут содержать финансовую информацию.',
    p2Before: 'Управляйте доступом с помощью доступных ', p2Strong: 'настроек общего доступа',
    p2After: ' и отправляйте ссылки только тем, кто должен увидеть результат.',
  },
  protectedSection: { items: [
    { lead: 'Размещённые сервисы.', rest: 'MyCIO использует размещённый Supabase для учётных записей, базы данных и хранилища, наряду с хостингом приложения и другими поставщиками услуг.' },
    { lead: 'Контроль доступа.', rest: 'Аутентификация и политики доступа к базе данных ограничивают доступ к сведениям учётной записи; поставщики услуг также обрабатывают данные, необходимые для работы сервиса.' },
    { lead: 'Обработка у каждого поставщика.', rest: 'Обработка данных поставщиками услуг описана в Политике конфиденциальности. Если вам нужны подробности до передачи конфиденциальных сведений, обратитесь в поддержку.' },
  ] },
  computedSection: { p: 'MyCIO объясняет смоделированные результаты и помогает наметить следующие шаги. Он не совершает сделок, не перемещает деньги и не заменяет специалиста по инвестициям, налогам или праву. Решения принимаете вы и проверяете важные действия перед их выполнением.' },
  ctas: { sample: 'Посмотреть пример', start: 'Начать с вопроса', note: 'Примеры носят иллюстративный характер; их допущения и ограничения показаны рядом.' },
}

const zh: MyCIOYourDataPageCopy = {
  metaTitle: '你的数据 · MyCIO.ai',
  metaDescription: 'MyCIO 如何处理规划信息、托管存储、付款以及服务提供商。',
  back: '← 返回',
  eyebrow: '你的信息，由你决定',
  titleBefore: '从你',
  titleEm: '愿意分享的内容',
  titleAfter: '开始。',
  intro: '你可以先用估算数字和一个财务问题开始。MyCIO 会根据你提供的信息，说明其支持的选项，并帮助你规划切实可行的下一步。开始使用无需连接银行。',
  contentsLabel: '目录',
  toc: {
    nothingToConnect: '无需连接任何账户',
    whatIsStored: '会保存哪些信息',
    whatIsNeverStored: '你无需提供的信息',
    roughIsFine: '粗略数字同样有效',
    deleteEverything: '查看、编辑或申请删除',
    sharing: '分享——在你准备好时',
    protectedHow: '存储与服务提供商',
    computed: '计算结果，而非建议',
  },
  nothingToConnect: {
    p1: '由你自己输入余额、收入、目标和日期。制定计划时，你无需向 MyCIO 提供银行密码或券商账户登录信息。',
    p2: '如果你通过其他助手使用 MyCIO，你经该助手发送的信息同样受其提供商的条款和隐私设置约束。',
  },
  whatIsStored: {
    lead: '用于提供服务的信息可能包括：',
    items: ['账户与登录信息。', '你输入或确认的家庭及财务信息。',
      '已保存的计划、对比结果和对话上下文。', '运营服务和处理购买所需的运营记录。'],
    provenanceBefore: '请在', provenanceStrong: '“你的信息与来源”',
    provenanceAfter: '中核对规划信息，并更正任何与你实际情况不符之处。数据处理详情请参阅',
    privacyLinkLabel: '隐私政策', provenanceEnd: '。',
  },
  whatIsNeverStored: {
    items: [
      { lead: '银行密码。', rest: '请勿在问题或文件中填写银行或券商的登录凭证。' },
      { lead: '已连接的银行账户。', rest: '你可以手动输入财务信息。' },
      { lead: '完整的支付卡信息。', rest: '银行卡付款由我们的支付服务商 Paddle 处理；我们从不查看或存储你的完整卡号。MyCIO 仅接收提供服务和客户支持所需的购买状态及交易参考信息。' },
    ],
    closing: '托管、身份验证、支付、错误监控和 AI 服务提供商会处理提供服务所需的信息。可选的分析服务提供商只有在你允许分析后才会收到数据。隐私政策说明了这些角色。',
  },
  roughIsFine: {
    p1Before: '估算数字可以帮助你起步。在依据某项对比之前，请先核对', p1Strong: '假设和缺失的信息',
    p1After: '。',
    p2: '有些问题需要准确的日期、账户归属或税务信息。MyCIO 可能会向你询问这些信息，或说明某个情景超出其覆盖范围。',
  },
  deleteSection: {
    p1: '使用计划中的控件查看或删除单项规划信息。你也可以在“设置”中下载你的数据，或申请删除你的账户及有效的规划数据。',
    p2: '删除账户后，少量付款、防欺诈、安全或法律相关记录可能需要保留；服务提供商备份中的数据删除遵循其备份周期。隐私政策说明了这些例外情况；如有具体请求，请联系支持团队。',
  },
  sharingSection: {
    heading: '自主选择分享内容',
    p1Before: '在把链接发给伴侣或专业人士之前，请先查看', p1Strong: '共享的计划摘要',
    p1After: '；摘要中可能包含财务信息。',
    p2Before: '使用现有的', p2Strong: '分享控制',
    p2After: '管理访问权限，并且只把链接分享给你希望看到结果的人。',
  },
  protectedSection: { items: [
    { lead: '托管服务。', rest: 'MyCIO 使用托管的 Supabase 提供账户、数据库和存储服务，同时使用应用托管及其他服务提供商。' },
    { lead: '访问控制。', rest: '身份验证和数据库访问策略限制对账户信息的访问；服务提供商也会处理运营服务所需的数据。' },
    { lead: '各服务提供商的处理方式。', rest: '隐私政策介绍了服务提供商的数据处理情况。如需在分享敏感信息前了解详情，请咨询支持团队。' },
  ] },
  computedSection: { p: 'MyCIO 解释模型测算的结果，并帮助你确定下一步。它不进行交易、不转移资金，也不能替代投资、税务或法律专业人士。由你决定如何行动，并在执行重要操作前自行核实。' },
  ctas: { sample: '查看示例', start: '从一个问题开始', note: '示例仅作说明之用，其假设和局限会一并列出。' },
}

const ja: MyCIOYourDataPageCopy = {
  metaTitle: 'あなたのデータ · MyCIO.ai',
  metaDescription: 'MyCIO による計画情報、ホスティング型ストレージ、支払い、サービス提供事業者の取り扱いについて。',
  back: '← 戻る',
  eyebrow: 'あなたの情報は、あなたが選ぶ',
  titleBefore: 'まずは',
  titleEm: '共有してもよいと思える範囲',
  titleAfter: 'から。',
  intro: '概算の数字とお金に関する質問ひとつから始められます。MyCIO はご提供いただいた情報をもとに、対応している選択肢を説明し、実行可能な次のステップの計画をお手伝いします。始めるのに銀行口座の連携は不要です。',
  contentsLabel: '目次',
  toc: {
    nothingToConnect: '連携は不要',
    whatIsStored: '保存される情報',
    whatIsNeverStored: 'ご提供いただく必要のない情報',
    roughIsFine: '概算の数字でも十分に使えます',
    deleteEverything: '確認・編集・削除の依頼',
    sharing: '共有は、準備ができてから',
    protectedHow: '保存とサービス提供事業者',
    computed: '計算結果であり、助言ではありません',
  },
  nothingToConnect: {
    p1: '残高、収入、目標、日付はご自身で入力してください。プランを作成するために、銀行のパスワードや証券口座のログイン情報を MyCIO に伝える必要はありません。',
    p2: '別のアシスタントを通じて MyCIO をご利用の場合、そのアシスタント経由で送信した情報は、その提供事業者の利用規約とプライバシー設定の対象にもなります。',
  },
  whatIsStored: {
    lead: 'サービスの提供に使われる情報には、次のものが含まれる場合があります。',
    items: ['アカウント情報とログイン情報。', 'ご自身で入力または確認した世帯・財務に関する情報。',
      '保存したプラン、比較、会話の文脈。', 'サービスの運営と購入の管理に必要な業務記録。'],
    provenanceBefore: '計画情報は', provenanceStrong: '「あなたの情報と出典」',
    provenanceAfter: 'で確認し、実情と異なる点があれば修正してください。データの取り扱いの詳細は',
    privacyLinkLabel: 'プライバシーポリシー', provenanceEnd: 'をご覧ください。',
  },
  whatIsNeverStored: {
    items: [
      { lead: '銀行のパスワード。', rest: '質問や書類に、銀行や証券会社のログイン情報を含めないでください。' },
      { lead: '連携された銀行口座。', rest: '財務情報は手入力できます。' },
      { lead: '支払いカードの完全な情報。', rest: 'カード決済は当社の決済代行会社である Paddle が処理します。当社がカード番号の全桁を目にしたり保存したりすることはありません。MyCIO が受け取るのは、サービスの提供とサポートに必要な購入状況と取引の参照情報のみです。' },
    ],
    closing: 'ホスティング、認証、決済、エラー監視、AI の各サービス提供事業者は、サービスの提供に必要な情報を処理します。任意の分析サービス事業者がデータを受け取るのは、あなたが分析を許可した後に限られます。これらの役割はプライバシーポリシーで説明しています。',
  },
  roughIsFine: {
    p1Before: '概算の数字でも始められます。比較結果に頼る前に、', p1Strong: '前提条件と不足している情報',
    p1After: 'をご確認ください。',
    p2: '質問によっては、正確な日付、口座の名義、税務情報が必要です。MyCIO がそれらの情報をお尋ねしたり、そのシナリオが対応範囲外であることをご説明したりする場合があります。',
  },
  deleteSection: {
    p1: 'プラン内の操作で、個々の計画情報を確認・削除できます。設定では、データのダウンロードや、アカウントと有効な計画データの削除依頼も行えます。',
    p2: 'アカウント削除後も、支払い、不正防止、セキュリティ、法令上の記録の一部を保持する必要がある場合があります。また、提供事業者のバックアップからの削除は、そのバックアップの保存期間に従います。これらの例外はプライバシーポリシーで説明しています。具体的なご依頼はサポートまでご連絡ください。',
  },
  sharingSection: {
    heading: '共有する内容を選ぶ',
    p1Before: 'パートナーや専門家にリンクを送る前に、', p1Strong: '共有用のプラン概要',
    p1After: 'をご確認ください。概要には財務情報が含まれる場合があります。',
    p2Before: '利用できる', p2Strong: '共有設定',
    p2After: 'でアクセスを管理し、結果を見てほしい相手にだけリンクを共有してください。',
  },
  protectedSection: { items: [
    { lead: 'ホスティング型サービス。', rest: 'MyCIO は、アカウント、データベース、ストレージのサービスにホスティング型の Supabase を利用し、あわせてアプリケーションのホスティングやその他のサービス提供事業者を利用しています。' },
    { lead: 'アクセス制御。', rest: '認証とデータベースのアクセスポリシーにより、アカウント情報へのアクセスが制限されます。サービス提供事業者も、サービスの運営に必要なデータを処理します。' },
    { lead: '事業者ごとの取り扱い。', rest: 'サービス提供事業者による処理はプライバシーポリシーに記載しています。機微な情報を共有する前に詳細が必要な場合は、サポートにお問い合わせください。' },
  ] },
  computedSection: { p: 'MyCIO はモデルによる試算結果を説明し、次のステップを考えるお手伝いをします。売買の執行や資金の移動は行わず、投資・税務・法律の専門家に代わるものでもありません。何をするかはご自身で決め、重要な手続きは実行前にご確認ください。' },
  ctas: { sample: '例を見る', start: '質問から始める', note: '例は説明のためのものです。前提条件と限界は例と併せて表示されます。' },
}

const ko: MyCIOYourDataPageCopy = {
  metaTitle: '내 데이터 · MyCIO.ai',
  metaDescription: 'MyCIO가 계획 정보, 호스팅 저장소, 결제, 서비스 제공업체를 어떻게 다루는지 안내합니다.',
  back: '← 뒤로',
  eyebrow: '내 정보, 내가 선택',
  titleBefore: '',
  titleEm: '편하게 공유할 수 있는 것',
  titleAfter: '부터 시작하세요.',
  intro: '추정치와 재무 질문 하나로 시작할 수 있습니다. MyCIO는 제공하신 정보를 바탕으로 지원되는 선택지를 설명하고, 실천할 수 있는 다음 단계를 계획하도록 돕습니다. 시작할 때 은행 연결은 필요하지 않습니다.',
  contentsLabel: '목차',
  toc: {
    nothingToConnect: '연결할 것이 없습니다',
    whatIsStored: '저장되는 정보',
    whatIsNeverStored: '제공하지 않아도 되는 정보',
    roughIsFine: '대략적인 숫자도 충분합니다',
    deleteEverything: '확인, 수정 또는 삭제 요청',
    sharing: '공유는 준비되었을 때',
    protectedHow: '저장 및 서비스 제공업체',
    computed: '계산 결과이며 자문이 아닙니다',
  },
  nothingToConnect: {
    p1: '잔액, 소득, 목표, 날짜를 직접 입력하세요. 계획을 세우기 위해 MyCIO에 은행 비밀번호나 증권 계좌 로그인 정보를 제공할 필요가 없습니다.',
    p2: '다른 어시스턴트를 통해 MyCIO를 이용하는 경우, 그 어시스턴트를 통해 보내는 정보에는 해당 제공업체의 약관과 개인정보 설정도 적용됩니다.',
  },
  whatIsStored: {
    lead: '서비스 제공에 사용되는 정보에는 다음이 포함될 수 있습니다.',
    items: ['계정 및 로그인 정보.', '입력하거나 확인한 가구 및 재무 정보.',
      '저장된 계획, 비교 결과, 대화 맥락.', '서비스 운영과 구매 관리에 필요한 운영 기록.'],
    provenanceBefore: '', provenanceStrong: '“내 정보 및 출처”',
    provenanceAfter: '에서 계획 정보를 검토하고, 실제 상황과 다른 부분은 수정하세요. 데이터 처리에 관한 자세한 내용은 ',
    privacyLinkLabel: '개인정보 처리방침', provenanceEnd: '을 참고하세요.',
  },
  whatIsNeverStored: {
    items: [
      { lead: '은행 비밀번호.', rest: '질문이나 문서에 은행 또는 증권사 로그인 정보를 넣지 마세요.' },
      { lead: '연결된 은행 계좌.', rest: '재무 정보는 직접 입력할 수 있습니다.' },
      { lead: '결제 카드 전체 정보.', rest: '카드 결제는 당사의 결제 대행사인 Paddle이 처리합니다. 당사는 카드 번호 전체를 보거나 저장하지 않습니다. MyCIO는 서비스 제공과 지원에 필요한 구매 상태와 거래 참조 정보만 받습니다.' },
    ],
    closing: '호스팅, 인증, 결제, 오류 모니터링, AI 서비스 제공업체는 서비스 제공에 필요한 정보를 처리합니다. 선택적 분석 제공업체는 분석을 허용한 뒤에만 데이터를 받습니다. 개인정보 처리방침에서 이러한 역할을 설명합니다.',
  },
  roughIsFine: {
    p1Before: '추정치로도 시작할 수 있습니다. 비교 결과에 의존하기 전에 ', p1Strong: '가정과 누락된 정보',
    p1After: '를 검토하세요.',
    p2: '어떤 질문에는 정확한 날짜, 계좌 명의, 세무 정보가 필요합니다. MyCIO가 이런 정보를 요청하거나 해당 시나리오가 지원 범위를 벗어난다고 설명할 수 있습니다.',
  },
  deleteSection: {
    p1: '계획 안의 컨트롤로 개별 계획 정보를 검토하거나 삭제할 수 있습니다. 설정에서는 데이터를 다운로드하거나 계정 및 활성 계획 데이터의 삭제를 요청할 수도 있습니다.',
    p2: '계정 삭제 후에도 결제, 부정 방지, 보안 또는 법적 기록 일부는 보관해야 할 수 있으며, 제공업체 백업에서의 삭제는 해당 백업의 보관 주기를 따릅니다. 개인정보 처리방침에서 이러한 예외를 설명합니다. 구체적인 요청은 고객 지원에 문의하세요.',
  },
  sharingSection: {
    heading: '공유할 내용 선택',
    p1Before: '배우자나 전문가에게 링크를 보내기 전에 ', p1Strong: '공유 계획 요약',
    p1After: '을 검토하세요. 요약에는 재무 정보가 포함될 수 있습니다.',
    p2Before: '제공되는 ', p2Strong: '공유 설정',
    p2After: '으로 접근 권한을 관리하고, 결과를 보여 줄 사람에게만 링크를 공유하세요.',
  },
  protectedSection: { items: [
    { lead: '호스팅 서비스.', rest: 'MyCIO는 계정, 데이터베이스, 저장소 서비스에 호스팅형 Supabase를 사용하며, 애플리케이션 호스팅 및 기타 서비스 제공업체도 함께 이용합니다.' },
    { lead: '접근 통제.', rest: '인증과 데이터베이스 접근 정책이 계정 정보에 대한 접근을 제한합니다. 서비스 제공업체도 서비스 운영에 필요한 데이터를 처리합니다.' },
    { lead: '제공업체별 처리.', rest: '개인정보 처리방침에 서비스 제공업체의 처리 내용이 설명되어 있습니다. 민감한 정보를 공유하기 전에 자세한 내용이 필요하면 고객 지원에 문의하세요.' },
  ] },
  computedSection: { p: 'MyCIO는 모델로 산출한 결과를 설명하고 다음 단계를 정리하도록 돕습니다. 매매를 실행하거나 자금을 이동하지 않으며, 투자·세무·법률 전문가를 대신하지 않습니다. 무엇을 할지는 직접 결정하고, 중요한 조치는 실행 전에 확인하세요.' },
  ctas: { sample: '예시 보기', start: '질문으로 시작하기', note: '예시는 설명을 위한 것이며, 가정과 한계가 함께 표시됩니다.' },
}

const ar: MyCIOYourDataPageCopy = {
  metaTitle: 'بياناتك · MyCIO.ai',
  metaDescription: 'كيف يتعامل MyCIO مع معلومات التخطيط والتخزين المستضاف والمدفوعات ومزوّدي الخدمات.',
  back: '→ رجوع',
  eyebrow: 'معلوماتك، وخياراتك',
  titleBefore: 'ابدأ بما ',
  titleEm: 'ترتاح لمشاركته',
  titleAfter: '.',
  intro: 'يمكنك البدء بتقديرات وسؤال مالي واحد. يستخدم MyCIO المعلومات التي تقدّمها لشرح الخيارات المدعومة ومساعدتك على التخطيط لخطوات تالية عملية. لا حاجة إلى ربط أي حساب مصرفي للبدء.',
  contentsLabel: 'المحتويات',
  toc: {
    nothingToConnect: 'لا شيء لربطه',
    whatIsStored: 'ما الذي يُحفظ',
    whatIsNeverStored: 'ما لا تحتاج إلى تقديمه',
    roughIsFine: 'الأرقام التقريبية معتبرة',
    deleteEverything: 'المراجعة أو التعديل أو طلب الحذف',
    sharing: 'المشاركة — حين تكون مستعدًا',
    protectedHow: 'التخزين ومزوّدو الخدمات',
    computed: 'نتائج محسوبة، لا مشورة',
  },
  nothingToConnect: {
    p1: 'أدخِل الأرصدة والدخل والأهداف والتواريخ بنفسك. لا تحتاج إلى إعطاء MyCIO كلمة مرور مصرفك أو بيانات الدخول إلى حساب الوساطة لإعداد خطة.',
    p2: 'إذا كنت تستخدم MyCIO عبر مساعد آخر، فإن المعلومات التي ترسلها عبر ذلك المساعد تخضع أيضًا لشروط مزوّده وإعدادات الخصوصية لديه.',
  },
  whatIsStored: {
    lead: 'قد تشمل المعلومات المستخدمة لتقديم الخدمة ما يلي:',
    items: ['معلومات الحساب وتسجيل الدخول.', 'بيانات الأسرة والبيانات المالية التي تُدخلها أو تؤكّدها.',
      'الخطط المحفوظة والمقارنات وسياق المحادثات.', 'السجلات التشغيلية اللازمة لتشغيل الخدمة وإدارة المشتريات.'],
    provenanceBefore: 'راجِع بيانات التخطيط في ', provenanceStrong: '«معلوماتك ومصادرها»',
    provenanceAfter: ' وصحّح كل ما لا يعكس وضعك. راجِع ',
    privacyLinkLabel: 'سياسة الخصوصية', provenanceEnd: ' لمعرفة تفاصيل معالجة البيانات.',
  },
  whatIsNeverStored: {
    items: [
      { lead: 'كلمات المرور المصرفية.', rest: 'لا تُدرج بيانات الدخول إلى المصرف أو شركة الوساطة في سؤال أو مستند.' },
      { lead: 'حساب مصرفي مربوط.', rest: 'يمكنك إدخال معلوماتك المالية يدويًا.' },
      { lead: 'البيانات الكاملة لبطاقة الدفع.', rest: 'تتولّى Paddle، مزوّد خدمات الدفع لدينا، معالجة المدفوعات بالبطاقة؛ ولا نطّلع أبدًا على رقم بطاقتك الكامل ولا نخزّنه. يتلقّى MyCIO فقط حالة الشراء ومراجع المعاملة اللازمة لتقديم الخدمة ودعمها.' },
    ],
    closing: 'يعالج مزوّدو خدمات الاستضافة والمصادقة والدفع ومراقبة الأخطاء والذكاء الاصطناعي المعلومات اللازمة لتقديم الخدمة. ولا يتلقّى مزوّدو التحليلات الاختيارية أي بيانات إلا بعد أن تسمح بالتحليلات. وتشرح سياسة الخصوصية هذه الأدوار.',
  },
  roughIsFine: {
    p1Before: 'تساعدك التقديرات على البدء. راجِع ', p1Strong: 'الافتراضات والتفاصيل الناقصة',
    p1After: ' قبل الاعتماد على أي مقارنة.',
    p2: 'تتطلّب بعض الأسئلة تواريخ دقيقة أو معرفة مالك الحساب أو تفاصيل ضريبية. قد يطلب MyCIO هذه المعلومات أو يوضّح أن سيناريو ما يقع خارج نطاق تغطيته.',
  },
  deleteSection: {
    p1: 'استخدم أدوات التحكم في خطتك لمراجعة بيانات التخطيط الفردية أو حذفها. ومن الإعدادات يمكنك أيضًا تنزيل بياناتك أو طلب حذف حسابك وبيانات التخطيط النشطة.',
    p2: 'قد يلزم الاحتفاظ بقدر محدود من سجلات الدفع أو منع الاحتيال أو الأمن أو السجلات القانونية بعد حذف الحساب، ويتبع الحذف من النسخ الاحتياطية لدى المزوّدين دورة حياة تلك النسخ. تشرح سياسة الخصوصية هذه الاستثناءات؛ تواصل مع الدعم بطلب محدّد.',
  },
  sharingSection: {
    heading: 'اختر ما تشاركه',
    p1Before: 'راجِع ', p1Strong: 'ملخص الخطة المشترك',
    p1After: ' قبل إرسال رابطه إلى شريكك أو إلى مختص؛ فقد تتضمّن الملخصات معلومات مالية.',
    p2Before: 'استخدم ', p2Strong: 'أدوات التحكم في المشاركة',
    p2After: ' المتاحة لإدارة الوصول، ولا تشارك الروابط إلا مع من تريد أن يطّلع على النتيجة.',
  },
  protectedSection: { items: [
    { lead: 'خدمات مستضافة.', rest: 'يستخدم MyCIO خدمة Supabase المستضافة لخدمات الحسابات وقاعدة البيانات والتخزين، إلى جانب استضافة التطبيق ومزوّدي خدمات آخرين.' },
    { lead: 'ضوابط الوصول.', rest: 'تقيّد المصادقة وسياسات الوصول إلى قاعدة البيانات الوصولَ إلى معلومات الحساب؛ كما يعالج مزوّدو الخدمات البيانات اللازمة لتشغيل الخدمة.' },
    { lead: 'معالجة خاصة بكل مزوّد.', rest: 'تصف سياسة الخصوصية المعالجة التي يجريها مزوّدو الخدمات. اسأل الدعم إذا احتجت إلى تفاصيل قبل مشاركة معلومات حساسة.' },
  ] },
  computedSection: { p: 'يشرح MyCIO النتائج المنمذجة ويساعدك على تحديد خطواتك التالية. لا ينفّذ صفقات ولا ينقل أموالًا ولا يحلّ محل مختص في الاستثمار أو الضرائب أو القانون. أنت من يقرّر ما يفعل، وتتحقّق من الإجراءات المهمة قبل تنفيذها.' },
  ctas: { sample: 'اطّلع على مثال', start: 'ابدأ بسؤال', note: 'الأمثلة توضيحية؛ وتُعرض افتراضاتها وحدودها إلى جانبها.' },
}

const hi: MyCIOYourDataPageCopy = {
  metaTitle: 'आपका डेटा · MyCIO.ai',
  metaDescription: 'MyCIO योजना संबंधी जानकारी, होस्टेड स्टोरेज, भुगतान और सेवा प्रदाताओं को कैसे संभालता है।',
  back: '← वापस',
  eyebrow: 'आपकी जानकारी, आपका चुनाव',
  titleBefore: 'उतने से शुरू करें जितना ',
  titleEm: 'साझा करने में आप सहज हों',
  titleAfter: '।',
  intro: 'आप अनुमानित आँकड़ों और एक वित्तीय सवाल से शुरुआत कर सकते हैं। MyCIO आपके दिए विवरणों से समर्थित विकल्पों को समझाता है और व्यावहारिक अगले कदमों की योजना बनाने में मदद करता है। शुरू करने के लिए किसी बैंक को जोड़ने की ज़रूरत नहीं है।',
  contentsLabel: 'विषय-सूची',
  toc: {
    nothingToConnect: 'कुछ भी जोड़ने की ज़रूरत नहीं',
    whatIsStored: 'क्या सहेजा जाता है',
    whatIsNeverStored: 'आपको क्या देने की ज़रूरत नहीं',
    roughIsFine: 'मोटे आँकड़े भी पूरी तरह चलते हैं',
    deleteEverything: 'देखें, बदलें या हटाने का अनुरोध करें',
    sharing: 'साझा करना — जब आप तैयार हों',
    protectedHow: 'स्टोरेज और सेवा प्रदाता',
    computed: 'गणना, सलाह नहीं',
  },
  nothingToConnect: {
    p1: 'बैलेंस, आय, लक्ष्य और तारीखें खुद दर्ज करें। योजना बनाने के लिए आपको MyCIO को बैंक का पासवर्ड या ब्रोकरेज लॉगिन देने की ज़रूरत नहीं है।',
    p2: 'अगर आप MyCIO को किसी दूसरे असिस्टेंट के ज़रिए इस्तेमाल करते हैं, तो उस असिस्टेंट से भेजी गई जानकारी पर उसके प्रदाता की शर्तें और प्राइवेसी सेटिंग्स भी लागू होती हैं।',
  },
  whatIsStored: {
    lead: 'सेवा देने के लिए इस्तेमाल होने वाली जानकारी में ये शामिल हो सकते हैं:',
    items: ['खाते और साइन-इन की जानकारी।', 'परिवार और वित्त से जुड़े वे तथ्य जो आप दर्ज या पुष्टि करते हैं।',
      'सहेजी गई योजनाएँ, तुलनाएँ और बातचीत का संदर्भ।', 'सेवा चलाने और खरीदारी संभालने के लिए ज़रूरी परिचालन रिकॉर्ड।'],
    provenanceBefore: 'योजना से जुड़े तथ्यों को ', provenanceStrong: '“आपकी जानकारी और स्रोत”',
    provenanceAfter: ' में जाँचें और जो भी आपकी स्थिति से मेल न खाए उसे ठीक करें। डेटा के प्रबंधन के विवरण के लिए ',
    privacyLinkLabel: 'प्राइवेसी पॉलिसी', provenanceEnd: ' देखें।',
  },
  whatIsNeverStored: {
    items: [
      { lead: 'बैंक पासवर्ड।', rest: 'किसी सवाल या दस्तावेज़ में बैंक या ब्रोकरेज की लॉगिन जानकारी न डालें।' },
      { lead: 'जुड़ा हुआ बैंक खाता।', rest: 'आप अपनी वित्तीय जानकारी खुद दर्ज कर सकते हैं।' },
      { lead: 'भुगतान कार्ड का पूरा विवरण।', rest: 'कार्ड भुगतान हमारा भुगतान प्रदाता Paddle संभालता है; हम आपका पूरा कार्ड नंबर न कभी देखते हैं, न सहेजते हैं। MyCIO को केवल सेवा देने और सहायता के लिए ज़रूरी खरीद की स्थिति और लेन-देन के संदर्भ मिलते हैं।' },
    ],
    closing: 'होस्टिंग, ऑथेंटिकेशन, भुगतान, एरर-मॉनिटरिंग और AI सेवा प्रदाता सेवा देने के लिए ज़रूरी जानकारी प्रोसेस करते हैं। वैकल्पिक एनालिटिक्स प्रदाताओं को डेटा तभी मिलता है जब आप एनालिटिक्स की अनुमति देते हैं। प्राइवेसी पॉलिसी इन भूमिकाओं को समझाती है।',
  },
  roughIsFine: {
    p1Before: 'अनुमान से शुरुआत आसान होती है। किसी तुलना पर भरोसा करने से पहले ', p1Strong: 'मान्यताओं और छूटे हुए विवरणों',
    p1After: ' को जाँच लें।',
    p2: 'कुछ सवालों के लिए सटीक तारीखें, खाते का स्वामित्व या टैक्स विवरण चाहिए। MyCIO ये तथ्य पूछ सकता है या बता सकता है कि कोई परिदृश्य उसके दायरे से बाहर है।',
  },
  deleteSection: {
    p1: 'योजना के अलग-अलग तथ्यों को देखने या हटाने के लिए अपनी योजना के कंट्रोल इस्तेमाल करें। सेटिंग्स से आप अपना डेटा डाउनलोड भी कर सकते हैं या अपने खाते और सक्रिय योजना डेटा को हटाने का अनुरोध कर सकते हैं।',
    p2: 'खाता हटाने के बाद भी भुगतान, धोखाधड़ी-रोकथाम, सुरक्षा या कानूनी ज़रूरतों से जुड़े कुछ सीमित रिकॉर्ड रखने पड़ सकते हैं, और प्रदाताओं के बैकअप से डेटा हटना उनके बैकअप चक्र के अनुसार होता है। प्राइवेसी पॉलिसी इन अपवादों को समझाती है; किसी खास अनुरोध के लिए सपोर्ट से संपर्क करें।',
  },
  sharingSection: {
    heading: 'तय करें कि क्या साझा करना है',
    p1Before: 'लिंक किसी साथी या पेशेवर को भेजने से पहले ', p1Strong: 'साझा योजना सारांश',
    p1After: ' देख लें; सारांश में वित्तीय जानकारी हो सकती है।',
    p2Before: 'पहुँच संभालने के लिए उपलब्ध ', p2Strong: 'शेयरिंग कंट्रोल',
    p2After: ' इस्तेमाल करें, और लिंक सिर्फ़ उन्हीं लोगों से साझा करें जिन्हें आप नतीजा दिखाना चाहते हैं।',
  },
  protectedSection: { items: [
    { lead: 'होस्टेड सेवाएँ।', rest: 'MyCIO खाते, डेटाबेस और स्टोरेज सेवाओं के लिए होस्टेड Supabase का इस्तेमाल करता है, साथ में ऐप्लिकेशन होस्टिंग और अन्य सेवा प्रदाताओं का भी।' },
    { lead: 'एक्सेस कंट्रोल।', rest: 'ऑथेंटिकेशन और डेटाबेस एक्सेस नीतियाँ खाते की जानकारी तक पहुँच सीमित करती हैं; सेवा प्रदाता भी सेवा चलाने के लिए ज़रूरी डेटा प्रोसेस करते हैं।' },
    { lead: 'हर प्रदाता का अपना प्रबंधन।', rest: 'प्राइवेसी पॉलिसी सेवा प्रदाताओं की प्रोसेसिंग का वर्णन करती है। संवेदनशील जानकारी साझा करने से पहले विवरण चाहिए तो सपोर्ट से पूछें।' },
  ] },
  computedSection: { p: 'MyCIO मॉडल किए गए नतीजों को समझाता है और अगले कदम तय करने में मदद करता है। यह ट्रेड नहीं करता, पैसा नहीं भेजता और निवेश, टैक्स या कानूनी पेशेवर की जगह नहीं लेता। क्या करना है यह आप तय करते हैं, और ज़रूरी कदम उठाने से पहले उन्हें जाँच लेते हैं।' },
  ctas: { sample: 'एक उदाहरण देखें', start: 'एक सवाल से शुरू करें', note: 'उदाहरण केवल समझाने के लिए हैं; उनकी मान्यताएँ और सीमाएँ साथ में दिखाई जाती हैं।' },
}

const id: MyCIOYourDataPageCopy = {
  metaTitle: 'Data Anda · MyCIO.ai',
  metaDescription: 'Cara MyCIO menangani informasi perencanaan, penyimpanan terkelola (hosted), pembayaran, dan penyedia layanan.',
  back: '← Kembali',
  eyebrow: 'Informasi Anda, pilihan Anda',
  titleBefore: 'Mulailah dari yang Anda ',
  titleEm: 'nyaman untuk dibagikan',
  titleAfter: '.',
  intro: 'Anda bisa mulai dengan perkiraan dan satu pertanyaan keuangan. MyCIO menggunakan detail yang Anda berikan untuk menjelaskan pilihan yang didukung dan membantu Anda merencanakan langkah berikutnya yang praktis. Tidak perlu menghubungkan bank untuk memulai.',
  contentsLabel: 'Daftar isi',
  toc: {
    nothingToConnect: 'Tidak ada yang perlu dihubungkan',
    whatIsStored: 'Apa yang disimpan',
    whatIsNeverStored: 'Yang tidak perlu Anda berikan',
    roughIsFine: 'Angka perkiraan sudah memadai',
    deleteEverything: 'Tinjau, ubah, atau minta penghapusan',
    sharing: 'Berbagi — saat Anda siap',
    protectedHow: 'Penyimpanan dan penyedia layanan',
    computed: 'Hasil perhitungan, bukan nasihat',
  },
  nothingToConnect: {
    p1: 'Masukkan sendiri saldo, penghasilan, tujuan, dan tanggal. Anda tidak perlu memberikan kata sandi bank atau login akun efek kepada MyCIO untuk menyusun rencana.',
    p2: 'Jika Anda menggunakan MyCIO melalui asisten lain, informasi yang Anda kirim melalui asisten tersebut juga tunduk pada ketentuan dan pengaturan privasi penyedianya.',
  },
  whatIsStored: {
    lead: 'Informasi yang digunakan untuk menyediakan layanan dapat mencakup:',
    items: ['Informasi akun dan masuk.', 'Fakta rumah tangga dan keuangan yang Anda masukkan atau konfirmasi.',
      'Rencana tersimpan, perbandingan, dan konteks percakapan.', 'Catatan operasional yang diperlukan untuk menjalankan layanan dan mengelola pembelian.'],
    provenanceBefore: 'Tinjau fakta perencanaan di ', provenanceStrong: '“Informasi & sumber Anda”',
    provenanceAfter: ' dan perbaiki apa pun yang tidak mencerminkan situasi Anda. Lihat ',
    privacyLinkLabel: 'Kebijakan Privasi', provenanceEnd: ' untuk rincian penanganan data.',
  },
  whatIsNeverStored: {
    items: [
      { lead: 'Kata sandi bank.', rest: 'Jangan cantumkan kredensial bank atau perusahaan efek dalam pertanyaan atau dokumen.' },
      { lead: 'Rekening bank yang terhubung.', rest: 'Anda dapat memasukkan informasi keuangan secara manual.' },
      { lead: 'Detail lengkap kartu pembayaran.', rest: 'Pembayaran kartu ditangani oleh Paddle, penyedia pembayaran kami; kami tidak pernah melihat atau menyimpan nomor kartu Anda secara lengkap. MyCIO hanya menerima status pembelian dan referensi transaksi yang diperlukan untuk menyediakan dan mendukung layanan.' },
    ],
    closing: 'Penyedia layanan hosting, autentikasi, pembayaran, pemantauan galat, dan AI memproses informasi yang diperlukan untuk menyediakan layanan. Penyedia analitik opsional hanya menerima data setelah Anda mengizinkan analitik. Kebijakan Privasi menjelaskan peran-peran ini.',
  },
  roughIsFine: {
    p1Before: 'Perkiraan dapat membantu Anda memulai. Tinjau ', p1Strong: 'asumsi dan detail yang belum ada',
    p1After: ' sebelum mengandalkan sebuah perbandingan.',
    p2: 'Beberapa pertanyaan memerlukan tanggal pasti, kepemilikan akun, atau detail pajak. MyCIO dapat menanyakan fakta tersebut atau menjelaskan bahwa suatu skenario berada di luar cakupannya.',
  },
  deleteSection: {
    p1: 'Gunakan kontrol di rencana Anda untuk meninjau atau menghapus fakta perencanaan satu per satu. Di Pengaturan, Anda juga dapat mengunduh data atau meminta penghapusan akun dan data perencanaan aktif Anda.',
    p2: 'Sebagian kecil catatan pembayaran, pencegahan penipuan, keamanan, atau catatan hukum mungkin perlu disimpan setelah akun dihapus, dan penghapusan dari cadangan penyedia mengikuti siklus hidup cadangan tersebut. Kebijakan Privasi menjelaskan pengecualian ini; hubungi dukungan dengan permintaan yang spesifik.',
  },
  sharingSection: {
    heading: 'Pilih apa yang dibagikan',
    p1Before: 'Tinjau ', p1Strong: 'ringkasan rencana yang dibagikan',
    p1After: ' sebelum mengirim tautannya kepada pasangan atau profesional; ringkasan dapat memuat informasi keuangan.',
    p2Before: 'Gunakan ', p2Strong: 'kontrol berbagi',
    p2After: ' yang tersedia untuk mengatur akses, dan bagikan tautan hanya kepada orang yang memang Anda ingin melihat hasilnya.',
  },
  protectedSection: { items: [
    { lead: 'Layanan terkelola.', rest: 'MyCIO menggunakan Supabase yang di-hosting untuk layanan akun, basis data, dan penyimpanan, bersama hosting aplikasi dan penyedia layanan lainnya.' },
    { lead: 'Kontrol akses.', rest: 'Autentikasi dan kebijakan akses basis data membatasi akses ke informasi akun; penyedia layanan juga memproses data yang diperlukan untuk menjalankan layanan.' },
    { lead: 'Penanganan per penyedia.', rest: 'Kebijakan Privasi menjelaskan pemrosesan oleh penyedia layanan. Tanyakan kepada dukungan jika Anda memerlukan rincian sebelum membagikan informasi sensitif.' },
  ] },
  computedSection: { p: 'MyCIO menjelaskan hasil yang dimodelkan dan membantu Anda menyusun langkah berikutnya. MyCIO tidak melakukan transaksi, tidak memindahkan uang, dan tidak menggantikan profesional investasi, pajak, atau hukum. Anda yang memutuskan tindakan dan memverifikasi langkah penting sebelum melaksanakannya.' },
  ctas: { sample: 'Lihat contoh', start: 'Mulai dengan pertanyaan', note: 'Contoh bersifat ilustratif; asumsi dan batasannya ditampilkan di sampingnya.' },
}

const tr: MyCIOYourDataPageCopy = {
  metaTitle: 'Verileriniz · MyCIO.ai',
  metaDescription: 'MyCIO’nun planlama bilgilerini, barındırılan depolamayı, ödemeleri ve hizmet sağlayıcıları nasıl ele aldığı.',
  back: '← Geri',
  eyebrow: 'Bilgileriniz, tercihleriniz',
  titleBefore: '',
  titleEm: 'Paylaşmakta rahat olduğunuz bilgilerle',
  titleAfter: ' başlayın.',
  intro: 'Tahmini rakamlar ve tek bir finansal soruyla başlayabilirsiniz. MyCIO, verdiğiniz bilgileri desteklenen seçenekleri açıklamak ve uygulanabilir sonraki adımları planlamanıza yardımcı olmak için kullanır. Başlamak için banka bağlantısı gerekmez.',
  contentsLabel: 'İçindekiler',
  toc: {
    nothingToConnect: 'Bağlanacak bir şey yok',
    whatIsStored: 'Neler saklanır',
    whatIsNeverStored: 'Vermeniz gerekmeyenler',
    roughIsFine: 'Yaklaşık rakamlar da geçerlidir',
    deleteEverything: 'İnceleme, düzenleme veya silme talebi',
    sharing: 'Paylaşım — hazır olduğunuzda',
    protectedHow: 'Depolama ve hizmet sağlayıcılar',
    computed: 'Hesaplama, tavsiye değil',
  },
  nothingToConnect: {
    p1: 'Bakiyeleri, geliri, hedefleri ve tarihleri kendiniz girin. Plan oluşturmak için MyCIO’ya banka şifrenizi veya aracı kurum giriş bilgilerinizi vermeniz gerekmez.',
    p2: 'MyCIO’yu başka bir asistan üzerinden kullanıyorsanız, o asistan aracılığıyla gönderdiğiniz bilgiler ayrıca sağlayıcısının koşullarına ve gizlilik ayarlarına tabidir.',
  },
  whatIsStored: {
    lead: 'Hizmeti sunmak için kullanılan bilgiler şunları içerebilir:',
    items: ['Hesap ve oturum açma bilgileri.', 'Girdiğiniz veya onayladığınız hane ve finansal bilgiler.',
      'Kaydedilmiş planlar, karşılaştırmalar ve konuşma bağlamı.', 'Hizmeti yürütmek ve satın alımları yönetmek için gereken operasyonel kayıtlar.'],
    provenanceBefore: 'Planlama bilgilerini ', provenanceStrong: '“Bilgileriniz ve kaynakları”',
    provenanceAfter: ' bölümünde gözden geçirin ve durumunuzu yansıtmayan her şeyi düzeltin. Veri işleme ayrıntıları için ',
    privacyLinkLabel: 'Gizlilik Politikası', provenanceEnd: '’na bakın.',
  },
  whatIsNeverStored: {
    items: [
      { lead: 'Banka şifreleri.', rest: 'Bir soruya veya belgeye banka ya da aracı kurum giriş bilgilerini eklemeyin.' },
      { lead: 'Bağlı bir banka hesabı.', rest: 'Finansal bilgilerinizi elle girebilirsiniz.' },
      { lead: 'Ödeme kartının tüm bilgileri.', rest: 'Kartla ödemeler, ödeme hizmet sağlayıcımız Paddle tarafından yürütülür; kart numaranızın tamamını hiçbir zaman görmez ve saklamayız. MyCIO yalnızca hizmeti sunmak ve desteklemek için gereken satın alma durumunu ve işlem referanslarını alır.' },
    ],
    closing: 'Barındırma, kimlik doğrulama, ödeme, hata izleme ve yapay zekâ hizmet sağlayıcıları hizmetin sunulması için gereken bilgileri işler. İsteğe bağlı analiz sağlayıcıları ancak siz analize izin verdikten sonra veri alır. Gizlilik Politikası bu rolleri açıklar.',
  },
  roughIsFine: {
    p1Before: 'Tahminler başlamanıza yardımcı olur. Bir karşılaştırmaya güvenmeden önce ', p1Strong: 'varsayımları ve eksik bilgileri',
    p1After: ' gözden geçirin.',
    p2: 'Bazı sorular kesin tarihler, hesap sahipliği veya vergi bilgileri gerektirir. MyCIO bu bilgileri isteyebilir ya da bir senaryonun kapsamı dışında kaldığını açıklayabilir.',
  },
  deleteSection: {
    p1: 'Tek tek planlama bilgilerini incelemek veya kaldırmak için planınızdaki kontrolleri kullanın. Ayarlar’dan verilerinizi indirebilir ya da hesabınızın ve etkin planlama verilerinizin silinmesini talep edebilirsiniz.',
    p2: 'Hesap silindikten sonra ödeme, dolandırıcılığı önleme, güvenlik veya yasal yükümlülüklerle ilgili sınırlı kayıtların saklanması gerekebilir; sağlayıcı yedeklerinden silme ise o yedeklerin yaşam döngüsünü izler. Gizlilik Politikası bu istisnaları açıklar; belirli bir talep için destek ekibiyle iletişime geçin.',
  },
  sharingSection: {
    heading: 'Neyi paylaşacağınızı seçin',
    p1Before: 'Bağlantısını eşinize veya bir uzmana göndermeden önce ', p1Strong: 'paylaşılan plan özetini',
    p1After: ' gözden geçirin; özetler finansal bilgi içerebilir.',
    p2Before: 'Erişimi yönetmek için mevcut ', p2Strong: 'paylaşım kontrollerini',
    p2After: ' kullanın ve bağlantıları yalnızca sonucu görmesini istediğiniz kişilerle paylaşın.',
  },
  protectedSection: { items: [
    { lead: 'Barındırılan hizmetler.', rest: 'MyCIO; hesap, veritabanı ve depolama hizmetleri için barındırılan Supabase’i, uygulama barındırma ve diğer hizmet sağlayıcılarla birlikte kullanır.' },
    { lead: 'Erişim denetimleri.', rest: 'Kimlik doğrulama ve veritabanı erişim politikaları hesap bilgilerine erişimi kısıtlar; hizmet sağlayıcılar da hizmeti yürütmek için gereken verileri işler.' },
    { lead: 'Sağlayıcıya özgü işleme.', rest: 'Gizlilik Politikası hizmet sağlayıcıların veri işlemesini açıklar. Hassas bilgileri paylaşmadan önce ayrıntıya ihtiyacınız olursa destek ekibine sorun.' },
  ] },
  computedSection: { p: 'MyCIO modellenen sonuçları açıklar ve sonraki adımlarınızı belirlemenize yardımcı olur. İşlem yapmaz, para aktarmaz ve bir yatırım, vergi ya da hukuk uzmanının yerini tutmaz. Ne yapacağınıza siz karar verir, önemli adımları uygulamadan önce doğrularsınız.' },
  ctas: { sample: 'Bir örnek görün', start: 'Bir soruyla başlayın', note: 'Örnekler açıklama amaçlıdır; varsayımları ve sınırları yanlarında gösterilir.' },
}

const yourDataPageByLanguage: Partial<Record<MyCIOLanguageCode, MyCIOYourDataPageCopy>> = {
  en, es, pt, fr, de, it, ru, zh, ja, ko, ar, hi, id, tr,
}

export function getMyCIOYourDataPageCopy(language: string | null | undefined): MyCIOYourDataPageCopy {
  return pickCopy(getMyCIOLanguage(language).code, yourDataPageByLanguage, en)
}
